import { Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { getHierarchicalIdentifier, unwrapConnection } from "@utils/utils";
import React from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";
import { Badge } from "@fronterahealth/frontera-ui-components/ui/badge";

import {
  ApiReportReportStatusChoices,
  ReportDomainType,
  ReportGoalType,
  ReportTargetType,
} from "@api/graphql/types-and-hooks";
import { useSuggestionCount } from "@components/GoalsSections/ReviewGoalHelper";
import { useReviewAnchors } from "@components/QaReview/hooks/useReviewAnchors";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { TargetProgressGraph } from "@components/TargetPanel/TargetProgressGraph";
import { TreatmentPlanForm } from "@components/TreatmentPlanFieldForm/TreatmentPlanForm";
import { useInlineTreatmentPlanEdit } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { targetToFieldValues } from "@components/TreatmentPlanFieldForm/utils";
import { ReportNode } from "@providers/ReportProvider";

interface TemplateTargetReviewCardProps {
  target: ReportTargetType;
  goal: ReportGoalType;
  report: ReportNode;
  domain: ReportDomainType | null;
  targetFields: ReturnType<typeof useTreatmentPlanTemplate>["targetFields"];
  targetIndex: number;
  goalIndex: number;
  showChart?: boolean;
  onSuccess?: () => void;
}

export const TemplateTargetReviewCard: React.FC<TemplateTargetReviewCardProps> = ({
  target,
  goal,
  domain,
  report,
  targetFields,
  targetIndex,
  goalIndex,
  showChart = false,
  onSuccess,
}) => {
  const learnerId = report.learner?.id || "";
  const reportIsCompleted = report.reportStatus === ApiReportReportStatusChoices.Completed;

  const getSuggestionCount = useSuggestionCount(target.id, "target");

  // PROA-2122: a collapsed target card unmounts its fields (and their inline
  // suggestion indicators), so a target with unresolved QA suggestions shows no
  // sign of it while collapsed — even though those suggestions still block
  // completion. Surface a count badge on the row header. This count is
  // data-driven (not gated on the sidebar being open or indicators being in the
  // DOM), so it works while the card is collapsed.
  const { countForGoalOrTarget } = useReviewAnchors();
  const { state: session, dispatch } = useReviewSession();
  const targetSuggestionCount = countForGoalOrTarget(target.id, "target");

  const { saveTargetFields, saveTargetCustomField } = useInlineTreatmentPlanEdit({
    goalId: goal.id,
    reportId: report.id,
    domainId: goal.reportDomain?.id || "",
    learnerId,
    targetId: target.id,
    onSuccess,
  });

  const [userToggledOpen, setUserToggledOpen] = React.useState<boolean | null>(null);
  // Whether this row is open, most to least authoritative:
  //  1. the walker jumped to a suggestion on this row — force open so the
  //     indicator mounts and can be scrolled into view (a manual collapse
  //     releases this lock; see handleCollapse);
  //  2. the clinician's own last collapse/expand of this row;
  //  3. auto-expand when the row has unresolved QA suggestions. A collapsed card
  //     unmounts its fields (and their inline indicators), so its suggestions get
  //     counted in the header/row badge but never mount an indicator — leaving
  //     them unreachable in the sidebar walker. Opening by default surfaces them.
  const walkerRequestedOpen = session.expandRequestId === target.id;
  const open = walkerRequestedOpen || (userToggledOpen ?? targetSuggestionCount > 0);

  // Collapsing must also release a walker lock pointing at THIS row — otherwise
  // walkerRequestedOpen keeps it pinned open and the chevron does nothing.
  const handleCollapse = () => {
    setUserToggledOpen(false);
    if (walkerRequestedOpen) dispatch({ type: "requestExpand", itemId: null });
  };

  const prefix = getHierarchicalIdentifier(goalIndex, targetIndex);

  return (
    <div className="relative flex w-full items-center rounded-md">
      <div className="flex w-full flex-col">
        <div
          className="relative flex w-full flex-col items-start justify-between bg-limestone-50 p-2 rounded-lg"
          data-target
          // Always-mounted anchor (survives collapse) so the suggestion walker can
          // locate this row's position and expand it when jumping to a suggestion
          // whose inline indicator is unmounted because the card is collapsed.
          data-suggestion-target-row={target.id}
        >
          <div className="flex w-full flex-row items-center justify-between gap-x-2">
            <Paragraph colorType="primary" displayType="loud" data-goal-title className="ph-no-capture">
              <span className="text-text-secondary">{prefix}</span>
              {target.targetName || "Untitled Target"}
            </Paragraph>
            <div className="flex shrink-0 items-center gap-x-2">
              {!open && targetSuggestionCount > 0 && (
                <button
                  type="button"
                  onClick={() => setUserToggledOpen(true)}
                  aria-label={`${targetSuggestionCount} unresolved Quality Review suggestion${
                    targetSuggestionCount === 1 ? "" : "s"
                  } on this target`}
                  className="cursor-pointer"
                >
                  <Badge variant="outline" size="sm" className="bg-transparent text-yellow-600">
                    <ExclamationTriangleIcon data-icon="inline-start" /> {targetSuggestionCount}
                  </Badge>
                </button>
              )}
              <div className="h-6 w-6 shrink-0">
                {open ? (
                  <ChevronUpIcon
                    onClick={handleCollapse}
                    className="absolute h-6 w-6 p-1 text-black hover:cursor-pointer"
                  />
                ) : (
                  <ChevronDownIcon
                    onClick={() => setUserToggledOpen(true)}
                    className="absolute h-6 w-6 p-1 text-black hover:cursor-pointer"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <Transition
          as="div"
          show={open}
          className="overflow-hidden w-full"
          enter="transition transition-[max-height] duration-500 ease-in"
          enterFrom="transform max-h-0"
          enterTo="transform max-h-screen"
          leave="transition transition-[max-height] duration-500 ease-out"
          leaveFrom="transform max-h-screen"
          leaveTo="transform max-h-0"
        >
          <div className="flex flex-col gap-y-2 w-full border border-limestone-100 p-4 rounded-b-lg">
            {showChart && (
              <TargetProgressGraph
                reportGoalId={goal.id}
                target={target}
                signedChartImageUrl={target.signedChartImageUrl || ""}
              />
            )}

            <TreatmentPlanForm
              templateFields={targetFields}
              nativeFieldValues={targetToFieldValues(target, "-")}
              onNativeFieldSave={saveTargetFields}
              onCustomFieldSave={saveTargetCustomField}
              onImageUploaded={onSuccess}
              customFields={unwrapConnection(target.customFields) ?? []}
              isReportCompleted={reportIsCompleted}
              domain={domain}
              itemId={target.id}
              getSuggestionCount={getSuggestionCount}
            />
          </div>
        </Transition>
      </div>
    </div>
  );
};
